import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import {
  API_ORIGIN,
  VITE_DATADOG_ENV,
  VITE_ENV_NAME,
  VITE_RELEASE_VERSION,
} from '@/shared/lib/env';
import { excludeDepStack } from '@/shared/lib/error';
import { isLgwanAccess, isOnlyLocalhost, isProd } from '@/shared/lib/system';
import type { LoginResponse } from '../../entities/authentication/api/generated/model';

const ignoreEnvs = ['local', 'mock', 'unknown'];
const ignoreHostnames = ['localhost'];
const ignoreVersions = ['unknown'];
const enableDatadog =
  !ignoreEnvs.includes(VITE_ENV_NAME) &&
  !ignoreHostnames.includes(location.hostname) &&
  !ignoreVersions.includes(VITE_RELEASE_VERSION);

const DatadogDefaultMetadata = {
  environment: VITE_DATADOG_ENV.split('-')[0],
  product: 'dnr',
};

// LGWAN閉域網からDatadogへの直接通信は不可のため、プロキシ経由で送信する
const datadogProxy =
  isLgwanAccess() || isOnlyLocalhost()
    ? (options: { path: string; parameters: string }) => {
        return `${location.origin}/datadog${options.path}?${options.parameters}`;
      }
    : undefined;

const initDatadog = () => {
  // Datadog RUM 初期化
  datadogRum.init({
    applicationId: '39c19b1f-2d84-403b-9819-fb40677c22b8',
    clientToken: 'puba2b45483d8be2cb305cbc645ae87810b',
    site: 'ap1.datadoghq.com',
    service: 'dnr',
    env: VITE_DATADOG_ENV,
    allowedTracingUrls: [
      { match: API_ORIGIN, propagatorTypes: ['datadog'] },
      { match: 'http://localhost:8100', propagatorTypes: ['datadog'] },
      { match: 'http://localhost', propagatorTypes: ['datadog'] },
    ],
    version: VITE_RELEASE_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 100,
    trackUserInteractions: true,
    beforeSend: (event, context) => {
      // collect a RUM resource's response headers
      if (
        event.context &&
        event.type === 'resource' &&
        event.resource.type === 'fetch' &&
        'response' in context &&
        context.response
      ) {
        event.context.responseHeaders = Object.fromEntries(context.response.headers);
      }

      return true;
    },
    trackResources: true,
    trackLongTasks: true,
    traceContextInjection: 'sampled',
    silentMultipleInit: true,
    compressIntakeRequests: true,
    storeContextsAcrossPages: true,
    // 本番の場合、基本的に mask する
    defaultPrivacyLevel: isProd() ? 'mask' : 'mask-user-input',
    enablePrivacyForActionName: true,
    useSecureSessionCookie: true,
    usePartitionedCrossSiteSessionCookie: true,
    trackSessionAcrossSubdomains: true,
    plugins: [reactPlugin({ router: false })],
    proxy: datadogProxy,
  });

  // Datadog RUM Session Replay 開始
  datadogRum.startSessionReplayRecording();
  datadogRum.setGlobalContext(DatadogDefaultMetadata);

  datadogLogs.init({
    clientToken: 'puba2b45483d8be2cb305cbc645ae87810b',
    site: 'ap1.datadoghq.com',
    service: 'dnr',
    env: VITE_DATADOG_ENV,
    version: VITE_RELEASE_VERSION,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    telemetrySampleRate: 100,
    silentMultipleInit: true,
    // ref. https://docs.datadoghq.com/ja/logs/log_collection/javascript/#%E3%82%B3%E3%83%B3%E3%83%86%E3%82%AD%E3%82%B9%E3%83%88%E3%81%AE%E3%83%A9%E3%82%A4%E3%83%95%E3%82%B5%E3%82%A4%E3%82%AF%E3%83%AB
    storeContextsAcrossPages: true,
    useSecureSessionCookie: true,
    usePartitionedCrossSiteSessionCookie: true,
    trackSessionAcrossSubdomains: true,
    proxy: datadogProxy,
    beforeSend: (log) => {
      const ignore =
        // 404
        (log.http && log.http.status_code === 404) ||
        // React Router or AxiosError not via logger
        (log.origin !== 'logger' &&
          // なぜか、log.error?.kind が 'AxiosError' にならない場合があるので、message や stack でも判定する
          (log.error?.message?.includes('AxiosError') ||
            log.error?.stack?.includes('AxiosError') ||
            log.error?.kind === 'AxiosError'));

      if (ignore) {
        return false;
      }

      // network に関するエラーは、warn にする
      const isNetworkError =
        log.origin === 'network' ||
        log.message?.includes('Network Error') ||
        log.error?.message?.includes('Network Error');
      if (isNetworkError || log.message.startsWith('Warning')) {
        log.status = 'warn';
      }

      // インフラ、ネットワークの問題であるエラーは、warn にする
      const isInfrastructureError = log.http && [502, 503, 504].includes(log.http.status_code);
      if (isInfrastructureError) {
        log.status = 'warn';
      }

      if (log.error?.kind === 'ZodError' && log.origin !== 'logger') {
        log.error.message = log.message;
        log.message = 'failed to parse';
      }

      // When using Datadog Error Tracking, the deps stack must be excluded for proper tracking for not logger.
      if (log.error) {
        log.error.stack = excludeDepStack(log.error.stack) ?? '';
      }
      return true;
    },
  });
  datadogLogs.setGlobalContext(DatadogDefaultMetadata);
};

if (enableDatadog) {
  initDatadog();
}

export const setUser = (loginResponse: LoginResponse) => {
  datadogRum.setUser({
    id: loginResponse.id.toString(),
    name: loginResponse.name,
    email: loginResponse.email,
    last_login: loginResponse.last_login,
  });
  datadogLogs.setUser({
    id: loginResponse.id.toString(),
    name: loginResponse.name,
    email: loginResponse.email,
    last_login: loginResponse.last_login,
  });
};
